import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost } from '../lib/api';
import { qk } from '../lib/queryKeys';
import type { BootstrapResponse } from '../types/api';

export interface ToggleFavoriteVars {
  projectId?: string;
  taskId?: string;
  on: boolean;
}

function applyToggle(boot: BootstrapResponse, v: ToggleFavoriteVars): BootstrapResponse {
  const flip = (ids: string[], id: string) =>
    v.on ? (ids.includes(id) ? ids : [...ids, id]) : ids.filter((x) => x !== id);
  if (v.projectId) return { ...boot, favoriteProjectIds: flip(boot.favoriteProjectIds, v.projectId) };
  if (v.taskId) return { ...boot, favoriteTaskIds: flip(boot.favoriteTaskIds, v.taskId) };
  return boot;
}

// Pin/unpin a project or task. The star flips instantly (optimistic edit of the
// cached bootstrap), rolls back on error, and re-syncs on settle.
export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: ToggleFavoriteVars) =>
      apiPost('/api/favorites', { projectId: v.projectId, taskId: v.taskId, on: v.on }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: qk.bootstrap });
      const prev = qc.getQueryData<BootstrapResponse>(qk.bootstrap);
      if (prev) qc.setQueryData<BootstrapResponse>(qk.bootstrap, applyToggle(prev, v));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.bootstrap, ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.bootstrap });
    },
  });
}
